import { useState, useEffect, useRef, useCallback } from 'react';
import { captureFrame } from './captureFrame';
import { exposureOk, faceDistanceOk } from './qualityChecks';
import { extractEdgeFeatures, compareEdgeFeatures } from './backgroundCheck';

const HOLD_MS = 600;

export function useAutoCapture({
  isAligned,
  faceDetected,
  faceBox,
  videoRef,
  lastBackgroundEdges = null,
  isSelfieStage = false,
  onCaptureResult,
  enabled = true
}) {
  const [state, setState] = useState("SEARCHING"); // SEARCHING, ALIGNING, TOO_FAR, SAME_BACKGROUND, HOLDING, CAPTURED
  const [holdProgress, setHoldProgress] = useState(0); // 0 to 1
  const [qualityWarning, setQualityWarning] = useState(null);

  const holdStartRef = useRef(null);
  const isCapturingRef = useRef(false);
  const animFrameRef = useRef(null);

  const performCapture = useCallback(async () => {
    const video = videoRef.current?.video || videoRef.current;
    if (!video || isCapturingRef.current) return;

    isCapturingRef.current = true;
    setState("CAPTURED");

    try {
      const result = await captureFrame(video, 0.95);
      const edgeFeatures = extractEdgeFeatures(video);
      if (onCaptureResult) {
        onCaptureResult({ ...result, edgeFeatures });
      }
    } catch (err) {
      console.warn("Capture frame error:", err.message);
    } finally {
      setTimeout(() => {
        isCapturingRef.current = false;
        holdStartRef.current = null;
        setHoldProgress(0);
        setState("ALIGNING");
      }, 1000);
    }
  }, [videoRef, onCaptureResult]);

  useEffect(() => {
    if (!enabled || isCapturingRef.current) return;

    const video = videoRef.current?.video || videoRef.current;

    // 1. Check face detection
    if (!faceDetected) {
      setState("SEARCHING");
      setQualityWarning(null);
      holdStartRef.current = null;
      setHoldProgress(0);
      return;
    }

    if (video && video.videoWidth) {
      // 2. Distance Check (faceBox.width / video.videoWidth >= 0.35)
      if (faceBox && !faceDistanceOk(faceBox.width, video.videoWidth)) {
        setState("TOO_FAR");
        setQualityWarning("Come closer.");
        holdStartRef.current = null;
        setHoldProgress(0);
        return;
      }

      // 3. Background Check for Selfies (avgDiff < 15 triggers Red state)
      if (isSelfieStage && lastBackgroundEdges) {
        const curEdges = extractEdgeFeatures(video);
        const diff = compareEdgeFeatures(curEdges, lastBackgroundEdges);
        if (diff < 15) {
          setState("SAME_BACKGROUND");
          setQualityWarning("Same background detected. Please move.");
          holdStartRef.current = null;
          setHoldProgress(0);
          return;
        }
      }

      // 4. Exposure Check
      if (!exposureOk(video)) {
        setState("ALIGNING");
        setQualityWarning("Move to better lighting");
        holdStartRef.current = null;
        setHoldProgress(0);
        return;
      }
    }
    setQualityWarning(null);

    // 5. Pose alignment check
    if (!isAligned) {
      setState("ALIGNING");
      holdStartRef.current = null;
      setHoldProgress(0);
      return;
    }

    // 6. All criteria passed: Neutral + Good Distance + Different Background -> HOLDING state
    setState("HOLDING");

    const tick = (now) => {
      if (!holdStartRef.current) {
        holdStartRef.current = now;
      }
      const elapsed = now - holdStartRef.current;
      const progress = Math.min(elapsed / HOLD_MS, 1);
      setHoldProgress(progress);

      if (elapsed >= HOLD_MS) {
        cancelAnimationFrame(animFrameRef.current);
        performCapture();
      } else {
        animFrameRef.current = requestAnimationFrame(tick);
      }
    };

    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [enabled, faceDetected, isAligned, faceBox, videoRef, lastBackgroundEdges, isSelfieStage, performCapture]);

  return {
    state,
    holdProgress,
    qualityWarning,
    triggerManualCapture: performCapture
  };
}
